import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft, CheckCircle2,
    ClipboardList, Target, Zap,
    FileText, Download, Share2
} from 'lucide-react';

const MethodologyTemplates: React.FC = () => {
    const [selectedMethod, setSelectedMethod] = useState<string>('DMAIC');

    const methodologies = [
        {
            id: 'DMAIC',
            name: 'DMAIC',
            description: 'Define, Measure, Analyze, Improve, Control',
            useCase: 'Process improvement and variability reduction',
            steps: [
                { name: 'Define', items: ['Problem Statement', 'Goal Statement', 'SIPOC Diagram', 'Project Charter'] },
                { name: 'Measure', items: ['Data Collection Plan', 'Baseline Metrics', 'Measurement System Analysis'] },
                { name: 'Analyze', items: ['Root Cause Analysis', 'Value Stream Mapping', 'Hypothesis Testing'] },
                { name: 'Improve', items: ['Solution Generation', 'Pilot Testing', 'Implementation Plan'] },
                { name: 'Control', items: ['Control Plan', 'Standard Operating Procedures', 'Benefit Validation'] }
            ]
        },
        {
            id: 'PDCA',
            name: 'PDCA',
            description: 'Plan, Do, Check, Act',
            useCase: 'Continuous improvement and standard work',
            steps: [
                { name: 'Plan', items: ['Identify Opportunity', 'Formulate Plan', 'Set Objectives'] },
                { name: 'Do', items: ['Implement Plan', 'Execute Small Scale Study'] },
                { name: 'Check', items: ['Review Results', 'Analyze Deviations'] },
                { name: 'Act', items: ['Standardize Solution', 'Plan for Next Cycle'] }
            ]
        },
        {
            id: 'A3',
            name: 'A3 Problem Solving',
            description: 'One-page report for systematic problem solving',
            useCase: 'Strategic alignment and management reporting',
            steps: [
                { name: 'Background', items: ['Context', 'Importance'] },
                { name: 'Current State', items: ['Visual depiction of the problem'] },
                { name: 'Target Condition', items: ['Desired outcome'] },
                { name: 'Gap Analysis', items: ['Root Cause Analysis'] },
                { name: 'Countermeasures', items: ['Proposed actions'] },
                { name: 'Implementation Plan', items: ['Who, What, When'] },
                { name: 'Follow-up', items: ['Verification of results'] }
            ]
        },
        {
            id: '4D',
            name: '4D Analysis',
            description: 'Define, Draft, Detail, Deliver',
            useCase: 'Quick response projects and engineering changes',
            steps: [
                { name: 'Define', items: ['Scope', 'Constraints'] },
                { name: 'Draft', items: ['Preliminary Design', 'Initial Concept'] },
                { name: 'Detail', items: ['Technical Specs', 'CAD/BOM Updates'] },
                { name: 'Deliver', items: ['Production Release', 'Validation'] }
            ]
        }
    ];

    const currentMethod = methodologies.find(m => m.id === selectedMethod) || methodologies[0];

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <Link to="/rca" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                    <ArrowLeft className="w-4 h-4" />
                    Back to RCA Tools
                </Link>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <ClipboardList className="w-8 h-8 text-blue-600" />
                            Methodology Templates
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Standardized frameworks for problem solving and process improvement
                        </p>
                    </div>

                    <div className="flex gap-3">
                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors">
                            <Download className="w-4 h-4" />
                            Export PDF
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            <Share2 className="w-4 h-4" />
                            Share with Team
                        </button>
                    </div>
                </div>
            </div>

            {/* Method Selector */}
            <div className="flex flex-wrap gap-4 mb-8">
                {methodologies.map(m => (
                    <button
                        key={m.id}
                        onClick={() => setSelectedMethod(m.id)}
                        className={`px-6 py-3 rounded-xl border-2 transition-all ${selectedMethod === m.id
                                ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-md'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                    >
                        <div className="font-bold text-lg">{m.name}</div>
                        <div className="text-xs opacity-80">{m.id === 'A3' ? 'Strategic Analysis' : 'Process Framework'}</div>
                    </button>
                ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
                {/* Method Overview */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-gray-600">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-blue-600" />
                            Methodology Overview
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Description</label>
                                <p className="mt-1 text-gray-700">{currentMethod.description}</p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Best Use Case</label>
                                <p className="mt-1 text-gray-700 font-medium">{currentMethod.useCase}</p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 italic text-sm text-blue-800">
                                This template is designed for collaborative team sessions. Ensure all stakeholders are represented in the analysis.
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-900 rounded-xl p-6 text-white shadow-lg">
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Zap className="w-5 h-5 text-amber-400" />
                            AI Methodology Coach
                        </h3>
                        <p className="text-slate-300 text-sm mb-4">
                            Need help choosing the right method or drafting content?
                        </p>
                        <button className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-semibold">
                            Ask Magnus AI
                        </button>
                    </div>
                </div>

                {/* Template Execution Area */}
                <div className="lg:col-span-2 space-y-6">
                    {currentMethod.steps.map((step, index) => (
                        <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="font-bold text-gray-900 flex items-center gap-3">
                                    <span className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm">
                                        {index + 1}
                                    </span>
                                    {step.name}
                                </h3>
                                <FileText className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="p-6">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {step.items.map((item, idx) => (
                                        <div key={idx} className="group relative">
                                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 group-hover:border-blue-200 group-hover:bg-blue-50/30 transition-all cursor-pointer">
                                                <div className="w-5 h-5 rounded border border-gray-300 bg-white flex items-center justify-center">
                                                    <CheckCircle2 className="w-4 h-4 text-transparent group-hover:text-blue-500 transition-colors" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">{item}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4">
                                    <textarea
                                        placeholder={`Add notes or evidence for ${step.name}...`}
                                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px]"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MethodologyTemplates;
